import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { TextField, MenuItem } from "@mui/material";
import useLoader from "../../../hooks/useLoader.js";
import useModal from "../../../hooks/useModal.js";
import { createPayrollTemplate, updatePayrollTemplate } from "../../../services/payrollTemplate.js";
import PayheadRowsEditor from "../shared/PayheadRowsEditor.jsx";
import PfRuleCard from "../shared/PfRuleCard.jsx";
import useActiveSalaryCategories from "../shared/useActiveSalaryCategories.js";
import { toPayheadPayload, computePayheadsError, defaultPfRule, toPfRulePayload, validatePfRuleFields } from "../shared/payheadUtils.js";

const PAY_BASIS_OPTIONS = [
  { label: "Any pay basis", value: "" },
  { label: "Daily", value: "DAILY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Bi-weekly", value: "BIWEEKLY" },
  { label: "Hourly", value: "HOURLY" },
];

const STATUS_OPTIONS = [
  { label: "Active", value: 1 },
  { label: "Inactive", value: 0 },
];

const AddEditPayrollTemplate = ({ refresh, existingField = null }) => {
  const { showLoader, hideLoader } = useLoader();
  const { hideModal } = useModal();
  const isEdit = Boolean(existingField);

  const [name, setName] = useState(existingField?.name || "");
  const [payBasis, setPayBasis] = useState(existingField?.pay_basis || "");
  const [status, setStatus] = useState(existingField?.status ?? 1);
  const [payheads, setPayheads] = useState(existingField?.payheads || []);
  const [pfRule, setPfRule] = useState(defaultPfRule(existingField || {}));
  const categories = useActiveSalaryCategories();
  // Derived from current values once Submit has been attempted (see `errors`
  // below), so correcting a field clears only that field's message.
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Recomputed on every render (not just on Submit), so the error
  // appears/updates/clears live as the admin edits a payhead - it was
  // previously only set inside validate() and left stale until the next
  // Submit click. No rate_amount concept for a template, so this is
  // always the structural check only.
  const payheadsError = useMemo(() => computePayheadsError(payheads, undefined, categories), [payheads, categories]);
  const pfRuleError = useMemo(() => validatePfRuleFields(pfRule, payheads), [pfRule, payheads]);

  const updatePfRule = (patch) => setPfRule((prev) => ({ ...prev, ...patch }));

  const computeErrors = () => {
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = "Template name is required";
    if (payheadsError) nextErrors.payheads = payheadsError;
    if (pfRuleError) nextErrors.pfRule = pfRuleError;
    return nextErrors;
  };

  const errors = submitted ? computeErrors() : {};

  const validate = () => {
    setSubmitted(true);
    return Object.keys(computeErrors()).length === 0;
  };

  const buildPayload = () => ({
    name: name.trim(),
    pay_basis: payBasis || null,
    status: Number(status),
    payheads: toPayheadPayload(payheads),
    ...toPfRulePayload(pfRule),
  });

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);
    showLoader();

    try {
      const payload = buildPayload();
      const response = isEdit
        ? await updatePayrollTemplate(existingField.id, payload)
        : await createPayrollTemplate(payload);
      toast.success(response?.data?.message || (isEdit ? "Template updated successfully!" : "Template added successfully!"));
      refresh?.();
      hideModal?.();
    } catch (error) {
      console.error("Error saving payroll template:", error);
      toast.error(error?.response?.data?.message || "Failed to save payroll template");
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Template Name <span className="text-danger">*</span>
          </label>
          <TextField
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="e.g. Standard Daily Wage"
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Pay Basis
          </label>
          <TextField select size="small" fullWidth value={payBasis} onChange={(e) => setPayBasis(e.target.value)}>
            {PAY_BASIS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <div className="form-text">Restrict this template to one pay basis, or leave as "Any" to offer it everywhere.</div>
        </div>

        <PayheadRowsEditor
          rows={payheads}
          onChange={setPayheads}
          categories={categories}
          error={payheadsError}
          pfRule={pfRule}
          onPfRuleChange={updatePfRule}
          title="Payheads"
          helpText="Define the Basic/HRA/DA/TA recipe (Fixed, % of Rate, % of Gross or % of Basic). There's no concrete Rate Amount here to preview against - it resolves once this template is applied to a real wage rate."
        />

        <PfRuleCard value={pfRule} onChange={updatePfRule} error={errors.pfRule} />

        <div className="col-md-6 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Status
          </label>
          <TextField select size="small" fullWidth value={status} onChange={(e) => setStatus(Number(e.target.value))}>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2" style={{ borderTop: "1px solid #e0e0e0", paddingTop: "12px" }}>
        <button type="button" onClick={hideModal} className="btn btn-secondary btn-sm" disabled={isSubmitting}>
          Cancel
        </button>
        <button type="button" className="btn btn-success btn-sm" disabled={isSubmitting} onClick={handleSubmit}>
          {isSubmitting ? "Saving..." : isEdit ? "Update" : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default AddEditPayrollTemplate;
